import { Suspense, useEffect, useState } from "react"
import {
  Outlet,
  Link,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { LogOut, Menu } from "lucide-react"

import { Card, CardContent, Skeleton } from "../components/ui"
import {
  adminBootstrapQueryOptions,
  api,
  ApiError,
  type StaffUser,
} from "../lib/api"
import { authClient } from "../lib/auth-client"
import { NAV } from "../lib/nav"
import {
  StaffForgotPassword,
  StaffInvite,
  StaffMfaChallenge,
  StaffResetPassword,
  StaffSignIn,
} from "./auth-pages"

function Brand() {
  return (
    <div className="text-lg font-semibold tracking-tight text-foreground">
      Prospera Office
    </div>
  )
}

const STAFF_ROLE_CACHE_KEY = "prospera-office-role"

function cachedStaffRole(): StaffUser["role"] {
  if (typeof window === "undefined") return "officer"
  const role = window.sessionStorage.getItem(STAFF_ROLE_CACHE_KEY)
  return role === "admin" || role === "super_admin" ? role : "officer"
}

function navigationFor(role: StaffUser["role"]) {
  return NAV.filter(
    (item) =>
      (!item.adminOnly || role === "admin" || role === "super_admin") &&
      (!item.superAdminOnly || role === "super_admin")
  )
}

function AdminBootScreen() {
  const path = useRouterState({ select: (state) => state.location.pathname })
  const navigation = navigationFor(cachedStaffRole())
  return (
    <div
      className="h-screen overflow-hidden bg-background text-foreground"
      aria-label="Loading staff workspace"
      role="status"
    >
      <header className="fixed inset-x-0 top-0 z-40 flex h-16 items-center border-b bg-card px-4 lg:px-6">
        <Brand />
      </header>
      <aside className="fixed top-16 bottom-0 left-0 z-30 hidden w-64 border-r border-sidebar-border bg-sidebar p-3 text-sidebar-foreground lg:block">
        <nav className="space-y-1">
          {navigation.map(({ label, href, icon: Icon }) => (
            <Link
              key={href}
              to={href}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${path.startsWith(href) ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-sidebar-foreground"}`}
            >
              <Icon className="size-4.5" />
              {label}
            </Link>
          ))}
        </nav>
      </aside>
      <main className="fixed inset-x-0 top-16 bottom-0 overflow-hidden lg:left-64">
        <div className="mx-auto max-w-[1440px] p-4 sm:p-6 lg:p-8">
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96 max-w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </main>
      <span className="sr-only">Loading staff workspace</span>
    </div>
  )
}

function AccessDenied({ message }: { message: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-5">
      <Card className="max-w-md">
        <CardContent className="text-center">
          <h1 className="text-xl font-semibold">Access restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">{message}</p>
          <button
            className="mt-5 rounded-lg border bg-card px-4 py-2 text-sm font-medium"
            onClick={() =>
              void authClient.signOut().then(async () => {
                queryClient.clear()
                await navigate({ to: "/login" })
              })
            }
          >
            Sign out
          </button>
        </CardContent>
      </Card>
    </div>
  )
}

function pageApiPath(pathname: string, search = ""): string | null {
  const params = new URLSearchParams(search)
  const id = params.get("id")
  const routeId = (prefix: string) =>
    pathname.startsWith(`${prefix}/`) && pathname !== `${prefix}/`
      ? pathname.slice(prefix.length + 1).replace(/\/$/, "")
      : null
  if (pathname.startsWith("/applications")) {
    const detailId = routeId("/applications") ?? id
    return detailId
      ? `/admin/applications/${encodeURIComponent(detailId)}`
      : withSearch("/admin/applications", params)
  }
  if (pathname.startsWith("/borrowers")) {
    const detailId = routeId("/borrowers") ?? id
    return detailId
      ? `/admin/borrowers/${encodeURIComponent(detailId)}`
      : withSearch("/admin/borrowers", params)
  }
  if (pathname.startsWith("/loans")) {
    const detailId = routeId("/loans") ?? id
    return detailId
      ? `/admin/loans/${encodeURIComponent(detailId)}`
      : withSearch("/admin/loans", params)
  }
  if (pathname.startsWith("/staff-activity"))
    return withSearch("/admin/staff/attendance", params)
  if (pathname.startsWith("/staff")) return withSearch("/admin/staff", params)
  if (pathname.startsWith("/audit")) return withSearch("/admin/audit", params)
  if (pathname.startsWith("/compliance"))
    return withSearch("/admin/compliance", params)
  if (pathname.startsWith("/settings")) return "/admin/settings"
  if (pathname.startsWith("/loan-engine")) return "/admin/loan-engine"
  if (pathname.startsWith("/customise")) return "/admin/customise"
  if (pathname.startsWith("/blog")) return withSearch("/admin/blog", params)
  if (pathname.startsWith("/testimonials"))
    return withSearch("/admin/testimonials", params)
  return null
}

function withSearch(path: string, params: URLSearchParams) {
  params.delete("id")
  params.delete("tab")
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

function Shell({ staff }: { staff: StaffUser }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useRouterState({ select: (state) => state.location })
  const path = location.pathname
  const allowedNav = navigationFor(staff.role)
  return (
    <div className="h-screen overflow-hidden bg-background text-foreground">
      <header className="fixed inset-x-0 top-0 z-40 flex h-16 items-center border-b bg-card px-4 lg:px-6">
        <button
          className="mr-3 rounded-lg p-2 hover:bg-muted lg:hidden"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
        >
          <Menu className="size-5" />
        </button>
        <Brand />
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden text-right sm:block">
            <p className="text-sm font-semibold">{staff.name}</p>
            <p className="text-xs text-muted-foreground capitalize">
              {staff.role}
            </p>
          </div>
          <button
            onClick={() =>
              void api("/admin/staff/sessions/end-current", { method: "POST" })
                .catch(() => undefined)
                .then(() => authClient.signOut())
                .then(async () => {
                  queryClient.clear()
                  await navigate({ to: "/login" })
                })
            }
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Sign out"
          >
            <LogOut className="size-5" />
          </button>
        </div>
      </header>
      <aside
        className={`fixed top-16 bottom-0 left-0 z-30 w-64 border-r border-sidebar-border bg-sidebar p-3 text-sidebar-foreground transition-transform lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <nav className="space-y-1">
          {allowedNav.map(({ label, href, icon: Icon }) => (
            <Link
              key={href}
              to={href}
              preload="intent"
              onMouseEnter={() => {
                const endpoint = pageApiPath(href)
                if (endpoint)
                  void queryClient.prefetchQuery({
                    queryKey: ["admin-api", endpoint],
                    queryFn: () => api(endpoint),
                    staleTime: 60_000,
                  })
              }}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${path.startsWith(href) ? "bg-sidebar-accent text-sidebar-accent-foreground" : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"}`}
            >
              <Icon className="size-4.5" />
              {label}
            </Link>
          ))}
        </nav>
      </aside>
      {mobileOpen && (
        <button
          className="fixed inset-0 top-16 z-20 bg-muted-foreground/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation"
        />
      )}
      <main className="fixed inset-x-0 top-16 bottom-0 overflow-x-hidden overflow-y-auto overscroll-contain lg:left-64">
        <div className="mx-auto max-w-[1440px] p-4 sm:p-6 lg:p-8">
          <Suspense
            fallback={
              <div
                className="space-y-4"
                role="status"
                aria-label="Loading page"
              >
                <Skeleton className="h-8 w-64" />
                <Skeleton className="h-4 w-96 max-w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  )
}

export function AdminApp() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const location = useRouterState({ select: (state) => state.location })
  const pathname = location.pathname
  const isPublicPath = [
    "/login",
    "/mfa",
    "/forgot-password",
    "/reset-password",
    "/invite",
  ].some((path) => pathname.startsWith(path))
  const bootstrapQuery = useQuery({
    ...adminBootstrapQueryOptions(pathname.startsWith("/dashboard")),
    enabled: !isPublicPath,
  })
  const staff = bootstrapQuery.data?.staff
  useEffect(() => {
    if (bootstrapQuery.data?.dashboard)
      queryClient.setQueryData(
        ["admin-api", "/admin/dashboard"],
        bootstrapQuery.data.dashboard
      )
  }, [bootstrapQuery.data, queryClient])
  useEffect(() => {
    if (staff) window.sessionStorage.setItem(STAFF_ROLE_CACHE_KEY, staff.role)
  }, [staff])
  useEffect(() => {
    if (isPublicPath || !staff) return
    const endpoint = pathname.startsWith("/dashboard")
      ? null
      : pageApiPath(pathname, location.searchStr)
    if (endpoint)
      void queryClient.prefetchQuery({
        queryKey: ["admin-api", endpoint],
        queryFn: () => api(endpoint),
        staleTime: 60_000,
      })
  }, [isPublicPath, location.searchStr, pathname, queryClient, staff])
  useEffect(() => {
    if (
      bootstrapQuery.error instanceof ApiError &&
      bootstrapQuery.error.status === 401 &&
      pathname !== "/login" &&
      pathname !== "/mfa"
    )
      void navigate({ to: "/login", replace: true })
  }, [navigate, pathname, bootstrapQuery.error])
  useEffect(() => {
    if (staff && pathname === "/login") void navigate({ to: "/dashboard" })
  }, [navigate, pathname, staff])
  useEffect(() => {
    if (
      staff?.mfaRequired &&
      !staff.twoFactorEnabled &&
      pathname !== "/profile" &&
      !pathname.startsWith("/profile/")
    )
      void navigate({ to: "/profile", replace: true })
  }, [navigate, pathname, staff])
  if (pathname.startsWith("/mfa")) return <StaffMfaChallenge />
  if (pathname.startsWith("/login"))
    return (
      <StaffSignIn
        onMfaRequired={() => void navigate({ to: "/mfa" })}
        onSuccess={async () => {
          await bootstrapQuery.refetch()
          await navigate({ to: "/dashboard" })
        }}
      />
    )
  if (pathname.startsWith("/forgot-password")) return <StaffForgotPassword />
  if (pathname.startsWith("/reset-password")) return <StaffResetPassword />
  if (pathname.startsWith("/invite")) return <StaffInvite />
  if (bootstrapQuery.isPending) return <AdminBootScreen />
  if (
    bootstrapQuery.error instanceof ApiError &&
    bootstrapQuery.error.status === 401
  )
    return (
      <StaffSignIn
        onMfaRequired={() => void navigate({ to: "/mfa" })}
        onSuccess={async () => {
          await bootstrapQuery.refetch()
          await navigate({ to: "/dashboard" })
        }}
      />
    )
  if (bootstrapQuery.error)
    return (
      <AccessDenied
        message={
          bootstrapQuery.error instanceof ApiError &&
          bootstrapQuery.error.status === 403
            ? "This account does not have staff access."
            : "Staff access could not be verified."
        }
      />
    )
  return staff ? <Shell staff={staff} /> : null
}
